import { useCanViewEmails } from '../../../helpers/useCanViewEmails';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import CloseIcon from '@mui/icons-material/Close';
import { useHotkeys } from 'react-hotkeys-hook';
import { useHistory } from 'react-router-dom';
import { useDebounce } from 'usehooks-ts';

import { getSubGroupsByGroupIdThunk } from '../../../store/subGroups/subGroupsSlice';
import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { patchGroupAnalysts } from '../../../api/groupsApi';
import SearchInput from '../../modules/SearchInput';
import styles from './LinkAnalystModal.module.scss';
import { getUsersByRolesArray } from '../../../api';
import { USER_NEW } from '../../../enums/TYPES';
import RolePlate from '../../modules/RolePlate';
import { RolesLowerCase } from '../../../enums';
import Avatar from '../../modules/Avatar';
import Modal from '../../modules/Modal';

type TLinkAnalystModal = {
  open: boolean;
  switchModal: () => void;
};

const LinkAnalystModal: React.FC<TLinkAnalystModal> = ({ open, switchModal }) => {
  const canViewEmails = useCanViewEmails();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { currentGroup, allAnalysts, sort, sortOption } = useAppSelector((state) => state.subGroupsStore);

  const [analystsToAdd, setAnalystsToAdd] = useState<string[]>([]);
  const [analystsToRemove, setAnalystsToRemove] = useState<string[]>([]);
  const [errMsg, setErrMsg] = useState('');
  const [assignedUsersIds, setAssignedUsersIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [allUsers, setAllUsers] = useState<USER_NEW[]>([]);
  const submitButtonRef = useRef<HTMLDivElement>(null);

  const debouncedValue = useDebounce<string>(search, 500);

  useEffect(() => {
    getUsersByRolesArray(
      Object.values(RolesLowerCase).filter((role) => role !== RolesLowerCase.SUPER_ADMIN),
      debouncedValue,
    ).then((res) => setAllUsers(res.documents));
  }, [currentGroup, debouncedValue]);

  useEffect(() => {
    if (currentGroup?.analysts.length || currentGroup?.viewers.length) {
      setAssignedUsersIds([...currentGroup.analysts.map((analyst) => analyst.id), ...currentGroup.viewers.map((viewer) => viewer.id)]);
    }
  }, [allAnalysts, currentGroup, debouncedValue, open]);

  const onSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrMsg('');

    if (e.target.checked) {
      setAnalystsToAdd(allUsers.map((user) => user.user.id).filter((elem) => !assignedUsersIds.includes(elem)));
      setAnalystsToRemove([]);
    } else {
      setAnalystsToAdd([]);
      setAnalystsToRemove(assignedUsersIds);
    }
  };

  const onChange = (e: any) => {
    setErrMsg('');
    if (e.target.checked) {
      const removeList = analystsToRemove.filter((id) => id !== e.target.id);
      const shouldAddUser = assignedUsersIds.includes(e.target.id);
      !shouldAddUser && setAnalystsToAdd([...analystsToAdd, e.target.id]);
      setAnalystsToRemove(removeList);
    } else {
      const addList = analystsToAdd.filter((id) => id !== e.target.id);
      setAnalystsToRemove([...analystsToRemove, e.target.id]);
      setAnalystsToAdd(addList);
    }
  };

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value);

  const onClose = () => {
    switchModal();
    setAnalystsToAdd([]);
    setAnalystsToRemove([]);
    setAssignedUsersIds([]);
    setLoading(false);
    setErrMsg('');
  };

  const onSubmit = async () => {
    setLoading(true);
    try {
      await patchGroupAnalysts(currentGroup!.id, { usersToAdd: analystsToAdd, usersToRemove: analystsToRemove });
      dispatch(getSubGroupsByGroupIdThunk({ groupId: history.location.state as string, sort, sortOption }));
      onClose();
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const userChips = useMemo(() => {
    const listOfChips = allUsers.filter(
      (u) => !analystsToRemove.includes(u.user.id) && (analystsToAdd.includes(u.user.id) || assignedUsersIds.includes(u.user.id)),
    );

    return listOfChips?.map((u) => (
      <div key={`chip_${u.user.id}`} className={styles.chip}>
        <span>{u?.user?.username ? u?.user?.username : (canViewEmails ? u?.user?.email : 'User')}</span>{' '}
        <CloseIcon
          onClick={() => onChange({ target: { id: u.user.id } })}
          className={styles.removeChip}
          sx={{ color: 'black', fontSize: '14px', marginLeft: '8px' }}
        />
      </div>
    ));
  }, [allUsers, currentGroup, analystsToAdd, assignedUsersIds, analystsToRemove, open]);

  useHotkeys('Enter', () => {
    if (!open || loading) return;

    submitButtonRef?.current?.click();
  });

  return (
    <Modal open={open} onClose={onClose} loading={loading}>
      <div className={styles.modalBody}>
        <div className={styles.modalHeader}>
          <div className={styles.title}>Edit Users</div>
        </div>
        <LinearProgress className={styles.progressBar} variant="determinate" value={100} />
        <div className={styles.modalContainer}>
          <SearchInput className={styles.searchInput} placeHolder="Search by email/name" onChange={onSearch} />
          <div className={styles.userChips}>{userChips}</div>
          <div className={styles.analystList}>
            <label htmlFor="selectAll">
              <div className={styles.userLine}>
                <div className={styles.userInfo}>
                  <div className={styles.info}>
                    <div className={styles.name}>Select All</div>
                  </div>
                </div>
                <input id="selectAll" onChange={(e) => onSelectAll(e)} type="checkbox" />
              </div>
            </label>
            {allUsers.map((analyst, i) => (
              <label htmlFor={analyst.user.id} key={`${analyst.user.id}_${i}`}>
                <div className={styles.userLine}>
                  <div className={styles.userInfo}>
                    <Avatar name={analyst.user.email} avatar={analyst.user.avatar_url} />
                    <div className={styles.info}>
                      <div className={styles.name}>{analyst.user.username}</div>
                      {canViewEmails && (<div className={styles.email}>{analyst.user.email}</div>)}
                    </div>
                  </div>
                  <div className={styles.role}>
                    <RolePlate userRole={analyst.role.title ? analyst.role.title : `${analyst.role}`} />
                  </div>
                  <input
                    onChange={onChange}
                    id={analyst.user.id}
                    type="checkbox"
                    checked={
                      (assignedUsersIds?.includes(analyst.user.id) && !analystsToRemove.includes(analyst.user.id)) ||
                      analystsToAdd.includes(analyst.user.id)
                    }
                  />
                </div>
              </label>
            ))}
          </div>
          <span className={styles.errMsg}>{errMsg}</span>
          <div className={styles.actionBtns}>
            <ContainedButton onClick={onSubmit} disabled={loading || (analystsToAdd.length === 0 && analystsToRemove.length === 0)}>
              <div ref={submitButtonRef}>Select</div>
            </ContainedButton>
            <OutlinedButton onClick={onClose}>Cancel</OutlinedButton>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default LinkAnalystModal;
